import React, { useState } from "react";
import ReactDOM from "react-dom";
import { BUTTON_TYPE } from '@bepure/tarkin';
import { fetchBigCommerceApi, HTTP_METHOD } from "utils/bigCommerceAuth";
import AddToCartBtn, {CartProduct} from "./AddToCartBtn";
import PartpayDiv from "./PartpayDiv";
import { ProductReview } from "./ProductsSection";
import ReadReviews from "./ReadReviews";
import StickyPurchaseButtons from "./StickyPurchaseButtons";
import ToggleableSubscription from "./ToggleableSubscription";
import PurchaseFrequencyDropdown from "./PurchaseFrequencyDropdown";

declare const BASE_API_URL: string;
declare const RECHARGE_PRODUCT_DATA: any[];


interface Props {
    product: ProductInformation;
    sku: string;
}

const ProductInformationWrapper = (props: Props) => {
    const [isSubscription, setIsSubscription] = useState(false);
    const [subscriptionFrequency, setSubscriptionFrequency] = useState(0);
    const soldCounter = props.product.customFields.edges.find(obj => obj.node.name === 'soldcount')?.node.value
    const supplyValue = props.product.customFields.edges.find(obj => obj.node.name === 'supplyValue')?.node.value
    const purchaseButtonText = isSubscription ? "Subscribe now" : "Add to Cart";
    const buyNowButtonText = isSubscription ? "Subscribe" : "Buy Now";
    const rechargeSubscriptionExists = !!RECHARGE_PRODUCT_DATA.find((x:any) => x.id === props.product.entityId)?.subscriptions.length
    const baseVariantId = props.product.variants.edges.values().next().value.node.entityId
    let cartProduct: CartProduct = {
        name: props.product.name,
        unit_price: props.product.prices.price.value,
        sku: props.sku,
        imageUrl: props.product.imageUrl,
        id: props.product.entityId,
        variantId: baseVariantId,
    }
    
    if(isSubscription){
        cartProduct = {
            ...cartProduct,
            chargeIntervalFrequecy: RECHARGE_PRODUCT_DATA.find((x:any) => x.id === props.product.entityId).subscriptions[0].charge_interval_frequency,
            orderIntervalFrequency: subscriptionFrequency,
            orderIntervalUnit: RECHARGE_PRODUCT_DATA.find((x:any) => x.id === props.product.entityId).subscriptions[0].order_interval_unit
        }  
    }
    if(!isSubscription){
        cartProduct = {
            name: props.product.name,
            unit_price: props.product.prices.price.value,
            sku: props.sku,
            imageUrl: props.product.imageUrl,
            id: props.product.entityId,
            variantId: baseVariantId,
        }
    }

    const paymentProviders = [
        {
            name: 'afterpay',
            payment_count: 4,
            company_logo: 'https://s3.ap-southeast-2.amazonaws.com/static.bepure.co.nz/Arwen/EveWellness/Icons/AfterPayMobile_38x24.svg',
            popup_image_link: 'https://static.bepure.co.nz/assets/partpay/Afterpay_NZ_ShopNow_Desktop-Lightbox_Mint%401x.png'
        },
        {
            name: 'layby',
            payment_count: 6,
            company_logo: 'https://s3.ap-southeast-2.amazonaws.com/static.bepure.co.nz/Arwen/EveWellness/Icons/LayBuyMobile_29x24.svg',
            popup_image_link: 'https://static.bepure.co.nz/assets/partpay/how_it_works_laybuy.png'
        },
    ];


    return (
        <div className="product-information-wrapper">
            <div className="product-top-line">
                <div className="product-name">
                    <div className="rich-text">
                        <p>{props.product.name}</p>
                    </div>
                </div>
                <div className="product-extra-info">
                    { supplyValue && <div className="price-container__supply-value">{supplyValue}</div>}
                    { soldCounter && <div className=""> {soldCounter + " tests taken"}</div> }
                </div>
            </div>
            <div className="extra-info-mobile">
                { supplyValue && <div className="price-container__supply-value">{supplyValue}</div>}
                { soldCounter && <div className=""> {soldCounter + " tests taken"}</div> }
            </div>
            <ReadReviews
                numReviews={props.product.reviewSummary.review_count}
                averageScore={props.product.reviewSummary.score || 0}
                hyperlink={'reviews/'}
                />
            <div className="price-container">

                <div className="price-container__price">
                    {props.product.prices.salePrice && 
                    <div className="strikeout space_5"> ${props.product.prices.retailPrice.value.toFixed(2) + " "}</div>}
                </div>

                <div className="price-container__price">
                    ${props.product.prices.price.value.toFixed(2)} 
                </div>
                

                {!isSubscription &&
                    <PartpayDiv
                    total={props.product.prices.price.value}
                    providers = {paymentProviders}/>
                }
                {isSubscription &&
                    <div className="part-pay-div">
                        {"Save 10% on your next order"}
                    </div>
                }
            </div>
            
            
            {!isSubscription && 
            <div className="part-pay-div-mobile">
                <PartpayDiv
                        total={props.product.prices.price.value}
                        providers = {paymentProviders}/>
            </div>}

            {rechargeSubscriptionExists &&
                <ToggleableSubscription productPrice={props.product.prices.price.value} 
                id={props.product.entityId} setSubscriptionState={setIsSubscription}></ToggleableSubscription>
            }

            
            
            {isSubscription &&
                <div>
                    <div className="frequency-dropdown-title">FREQUENCY</div>
                    <PurchaseFrequencyDropdown id={props.product.entityId} offerOneTimePurchase={!isSubscription} setFrequencyState={setSubscriptionFrequency}></PurchaseFrequencyDropdown>
                </div>
            }

            <div className="purchase-buttons">
                <AddToCartBtn product={cartProduct} sku={props.sku} otherClasses={'btn--primary--full main-atc-button'} textOverride={purchaseButtonText}/>
            </div>
            <StickyPurchaseButtons>
                <AddToCartBtn product={cartProduct} sku={props.sku} buttonType={BUTTON_TYPE.SECONDARY } textOverride={buyNowButtonText}/>
            </StickyPurchaseButtons>
        </div>
    )
}

const fetchProductReviewStats = async (sku: string) => {
    const response = await fetch(`${BASE_API_URL}/v1/service/product-management/products/reviews/${sku}`, {
        method: 'GET',
    })
    try {
        const reviews: ProductReviewResponse = await response.json();
        return Promise.resolve(reviews);
    } catch (error) {
        return Promise.reject({
            error: error.message,
        });
    }
}

const getResources = async (sku: string, currencyCode: string = 'NZD') => {
    const resources = await fetchBigCommerceApi(HTTP_METHOD.POST, new Headers(), {
        query: `
        query fetchProductImagesBySku($sku: String, $currencyCode: currencyCode) {
            site {
              product(sku: $sku) {
              name,
              plainTextDescription,
              defaultImage {
                imageUrl: url(width: 660)
              }
              prices (currencyCode: $currencyCode, includeTax: true) {
                price {
                ...MoneyFields
                }
                retailPrice {
                ...MoneyFields
                }
                salePrice {
                    ...MoneyFields
                }
              }    
              entityId,
              variants {
                edges {
                  node {
                    entityId,
                    
                  }
                }
              },
              customFields {
                edges {
                  node {
                    name
                    value
                  }
                }
              }
            }
          }
        }
        
        fragment MoneyFields on Money {
            value
          currencyCode
        }
        `,
        variables: {
            sku,
            currencyCode: currencyCode,
        }
    })
    return resources
}

interface Money {
    value: number;
    currencyCode: string;
}

interface CustomFields {
    edges: Node[];
}

interface Node {
    node: {
        name: string;
        value: string | number;
    }
}

export interface Variants {
    edges: VariantNode[];
}

interface VariantNode {
    node: {
        entityId: number
    }
}

export interface ProductInformation {
    name: string;
    plainTextDescription: string;
    reviewSummary: ProductReview;
    prices: {
        price: Money,
        retailPrice: Money,
        salePrice: Money,
    };
    imageUrl: string;
    entityId: number;
    customFields: CustomFields,
    variants: Variants
}

export interface ProductReviewResponse {
    data: ProductReview
}

export const getProductInformation = async (sku: string) => {
    const response = await getResources(sku, localStorage.currency);
    const reviewResponse: ProductReviewResponse = await fetchProductReviewStats(sku)
    const json = await response.json();
    console.log(">>", json.data.site)
    let productInformation: ProductInformation = {
        name: json.data.site?.product.name,
        plainTextDescription: json.data.site?.product.plainTextDescription,
        reviewSummary: reviewResponse.data,
        prices: json.data.site?.product.prices,
        imageUrl: json.data.site?.product.defaultImage.imageUrl,
        entityId: json.data.site?.product.entityId,
        customFields: json.data.site?.product.customFields,
        variants: json.data.site?.product.variants
    }
    return productInformation;
    
}


export default function render() {
    const elements = document.getElementsByClassName('product-data');
    Array.prototype.map.call(elements, (element: HTMLElement) => {
        const productSku = element.dataset.productSku || '';
        getProductInformation(productSku).then((productInfo: ProductInformation) => {
            if (productInfo) {
                return ReactDOM.render(<ProductInformationWrapper 
                                            product={productInfo}
                                            sku={productSku}/>
                                            , element);
            }
        })
    })
}